import { useCallback } from "react";
import { Glossary, GlossaryLocalePair } from "../lib";
import { useDeleteDeeplDictionary, usePutDeeplDictionary } from "../services";

export const useUpdateGlossary = () => {
    const { deleteDeeplDictionary: deleteDictionary } = useDeleteDeeplDictionary();
    const { putDeeplDictionary: putDictionary } = usePutDeeplDictionary();

    const updateGlossary = useCallback(
        (glossary: Glossary, file: File, newLocales: GlossaryLocalePair[]): Promise<void> => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();

                reader.onload = async () => {
                    try {
                        const fileContent = reader.result as string;
                        const dictionariesToPut: GlossaryLocalePair[] = newLocales;
                        const dictionariesToDelete: GlossaryLocalePair[] = [];

                        for (const oldPair of glossary.locales) {
                            if (
                                !newLocales.find(
                                    newPair =>
                                        newPair.source === oldPair.source &&
                                        newPair.target === oldPair.target
                                )
                            ) {
                                dictionariesToDelete.push(oldPair);
                            }
                        }

                        for (const localePair of dictionariesToPut) {
                            await putDictionary(glossary.id, localePair, fileContent);
                        }
                        for (const localePair of dictionariesToDelete) {
                            await deleteDictionary(glossary.id, localePair);
                        }

                        resolve();
                    } catch (error) {
                        if (error instanceof Error) {
                            reject(new Error(`Could not update glossary: ${error.message}`));
                        } else {
                            reject(new Error(`Could not update glossary: Unknown error`));
                        }
                    }
                };

                reader.onerror = error =>
                    reject(new Error("Could not update glossary: Failure to read file"));

                reader.readAsText(file);
            });
        },
        [deleteDictionary, putDictionary]
    );

    return { updateGlossary };
};
